import React, { useEffect, useMemo, useRef, useState } from 'react';
import api, { templatesAPI } from '../services/api';
import { getAiPresetForTemplate } from './TemplateStylePreview.jsx';
import { formatEventDateTimeDisplay } from '../utils/eventDateTime.js';
import './TemplateCustomizer.css';

const STEP_ORDER = ['layout', 'typography', 'colors', 'images', 'content', 'review', 'assemble'];

const STEP_META = {
  layout: { label: 'Layout', description: 'Confirm your base layout scaffold.' },
  typography: { label: 'Typography', description: 'Set global font choices and type-related values.' },
  colors: { label: 'Colors', description: 'Apply your palette and contrast choices.' },
  images: { label: 'Images', description: 'Upload or assign image assets.' },
  content: { label: 'Content', description: 'Provide messaging for each section.' },
  review: { label: 'Review', description: 'Verify all assigned values before final assembly.' },
  assemble: { label: 'Assemble', description: 'Preview scaffold or generate with AI.' },
};

const FONT_FAMILIES = [
  { value: 'Arial, Helvetica, sans-serif', label: 'Arial' },
  { value: '"Helvetica Neue", Helvetica, Arial, sans-serif', label: 'Helvetica Neue' },
  { value: 'Georgia, "Times New Roman", Times, serif', label: 'Georgia' },
  { value: '"Times New Roman", Times, serif', label: 'Times New Roman' },
  { value: '"Trebuchet MS", Tahoma, Verdana, Arial, sans-serif', label: 'Trebuchet MS' },
  { value: 'Verdana, Geneva, Tahoma, sans-serif', label: 'Verdana' },
  { value: '"Courier New", Courier, monospace', label: 'Courier New' },
  { value: 'Garamond, "Times New Roman", serif', label: 'Garamond' },
  { value: '"Palatino Linotype", "Book Antiqua", Palatino, serif', label: 'Palatino' },
  { value: 'Impact, Haettenschweiler, "Arial Narrow Bold", sans-serif', label: 'Impact' },
];

const typeKeywords = ['font', 'spacing', 'radius'];
const imageKeywords = ['image', 'icon', 'logo'];
const RSC_LOGO_WHITE_URL = 'https://rscwebsitestorage.blob.core.windows.net/$web/RSC Logo_white_1024x600.png';
const RSC_LOGO_BLUE_URL = 'https://rscwebsitestorage.blob.core.windows.net/$web/RSC Base Logo_1024x600_NEW.png';
const RSC_LOGOS = { white: RSC_LOGO_WHITE_URL, blue: RSC_LOGO_BLUE_URL };
const LIBRARY_FALLBACK_IMAGE_URL = `data:image/svg+xml,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="1200" height="675" viewBox="0 0 1200 675"><rect width="1200" height="675" fill="#e2e8f0"/><rect x="40" y="40" width="1120" height="595" rx="18" fill="#f8fafc" stroke="#cbd5e1" stroke-width="4"/><g fill="#64748b" font-family="Arial, Helvetica, sans-serif" text-anchor="middle"><text x="600" y="310" font-size="44" font-weight="700">Image unavailable</text><text x="600" y="356" font-size="24">Upload a replacement image from the Images step</text></g></svg>')}`;

const isImageField = (field) => {
  const key = String(field.key || '').toLowerCase();
  const label = String(field.label || '').toLowerCase();
  return field.type === 'image' || imageKeywords.some((term) => key.includes(term) || label.includes(term));
};

const isTypographyField = (field) => {
  const key = String(field.key || '').toLowerCase();
  if (field.type === 'select' && key.includes('font')) return true;
  return typeKeywords.some((term) => key.includes(term));
};

const isColorField = (field) => {
  const key = String(field.key || '').toLowerCase();
  return field.type === 'color' || key.includes('color');
};

const classifyField = (field) => {
  if (isImageField(field)) return 'images';
  if (isColorField(field)) return 'colors';
  if (isTypographyField(field)) return 'typography';
  return 'content';
};

const fieldValue = (field, customizations) => {
  const value = customizations[field.key];
  if (value === undefined || value === null || value === '') return field.default || '';
  return value;
};

const normalizeImagePreviewSrc = (value) => {
  const raw = String(value || '').trim().replace(/^['"]|['"]$/g, '');
  if (!raw) return '';

  if (/^(https?:\/\/|data:image\/|blob:)/i.test(raw)) return raw;
  if (raw.startsWith('//')) return `https:${raw}`;
  if (raw.startsWith('/')) return raw;

  // Common CDN/domain path without protocol
  if (/^[a-z0-9.-]+\.[a-z]{2,}(\/|$)/i.test(raw)) return `https://${raw}`;

  // Handle storage-relative paths that may still resolve in app context
  if (/^(templates|team)\//i.test(raw)) return `/${raw}`;

  return raw;
};

const resolveImageForOutput = (value, shouldForceFallback) => {
  const normalized = normalizeImagePreviewSrc(value);
  if (shouldForceFallback || !normalized) {
    return LIBRARY_FALLBACK_IMAGE_URL;
  }
  return normalized;
};

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const parseColorValue = (value) => {
  const raw = String(value || '').trim();
  const hexMatch = raw.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (hexMatch) {
    const hex = hexMatch[1].length === 3
      ? `#${hexMatch[1].split('').map((char) => `${char}${char}`).join('')}`
      : `#${hexMatch[1]}`;
    return { hex: hex.toLowerCase(), alpha: 1 };
  }

  const rgbaMatch = raw.match(/^rgba?\(([^)]+)\)$/i);
  if (rgbaMatch) {
    const parts = rgbaMatch[1].split(',').map((item) => item.trim());
    if (parts.length >= 3) {
      const r = clamp(parseInt(parts[0], 10) || 0, 0, 255);
      const g = clamp(parseInt(parts[1], 10) || 0, 0, 255);
      const b = clamp(parseInt(parts[2], 10) || 0, 0, 255);
      const a = parts.length >= 4 ? clamp(parseFloat(parts[3]) || 0, 0, 1) : 1;
      const toHex = (n) => n.toString(16).padStart(2, '0');
      return { hex: `#${toHex(r)}${toHex(g)}${toHex(b)}`, alpha: a };
    }
  }

  return { hex: '#000000', alpha: 1 };
};

const composeColorWithAlpha = (hex, alphaPercent) => {
  const color = parseColorValue(hex);
  const safePercent = clamp(Number(alphaPercent) || 100, 0, 100);
  if (safePercent >= 100) return color.hex;

  const normalized = safePercent / 100;
  const hexValue = color.hex.replace('#', '');
  const r = parseInt(hexValue.slice(0, 2), 16);
  const g = parseInt(hexValue.slice(2, 4), 16);
  const b = parseInt(hexValue.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${Number(normalized.toFixed(2))})`;
};

const summarizeFields = (items, customizations) => items.map((field) => {
  const value = String(fieldValue(field, customizations) || '').trim();
  const shortValue = value.length > 100 ? `${value.substring(0, 97)}...` : value;
  const imageSrc = isImageField(field) ? normalizeImagePreviewSrc(value) : '';
  return {
    key: field.key,
    label: field.label,
    value: shortValue || '(empty)',
    rawValue: value,
    imageSrc,
    isEmpty: !value,
  };
});

const injectGlobalFont = (html, selectedFont) => {
  if (!html || !selectedFont) return html;
  const safeFont = String(selectedFont).replace(/[<>]/g, '');
  const styleTag = `<style type="text/css">html, body, * , *::before, *::after { font-family: ${safeFont} !important; }</style>`;

  if (/<head[^>]*>/i.test(html)) {
    return html.replace(/<head([^>]*)>/i, `<head$1>${styleTag}`);
  }

  if (/<body[^>]*>/i.test(html)) {
    return html.replace(/<body([^>]*)>/i, `<body$1>${styleTag}`);
  }

  return `${styleTag}${html}`;
};

const injectRscLogo = (html, logoVariant) => {
  if (!logoVariant || !html) return html;
  const logoUrl = RSC_LOGOS[logoVariant];
  if (!logoUrl) return html;
  if (html.includes(logoUrl)) return html;
  const logoHtml = `<div style="text-align:left;padding:8px 0 12px;"><img src="${logoUrl}" alt="RSC Logo" style="display:block;width:120px;max-width:120px;height:auto;border:0;" /></div>`;

  if (/<body[^>]*>/i.test(html)) {
    return html.replace(/<body([^>]*)>/i, `<body$1>${logoHtml}`);
  }

  if (/<table[^>]*>/i.test(html)) {
    return html.replace(/<table([^>]*)>/i, `<table$1><tr><td>${logoHtml}</td></tr>`);
  }

  return `${logoHtml}${html}`;
};

const toText = (value) => String(value || '').trim();

const firstNonEmpty = (...values) => values.map((v) => toText(v)).find(Boolean) || '';

const buildUnlayerTextContent = (id, text, overrides = {}) => ({
  id,
  type: 'text',
  values: {
    text: toText(text),
    containerPadding: '0px',
    fontSize: '16px',
    lineHeight: '170%',
    color: '#1f2937',
    ...overrides
  }
});

const buildUnlayerImageContent = (id, src, width = '560px') => ({
  id,
  type: 'image',
  values: {
    src: {
      url: toText(src),
      width: null,
      height: null
    },
    altText: '',
    width,
    textAlign: 'center',
    containerPadding: '0px'
  }
});

const buildUnlayerButtonContent = (id, label, href, bgColor) => ({
  id,
  type: 'button',
  values: {
    text: toText(label),
    href: toText(href),
    textAlign: 'center',
    containerPadding: '0px',
    buttonColors: {
      color: '#ffffff',
      backgroundColor: toText(bgColor) || '#1f4d8f'
    },
    size: {
      autoWidth: true,
      width: 'auto'
    },
    border: {
      radius: 6
    }
  }
});

const buildUnlayerRow = (id, columns, values = {}) => ({
  id,
  cells: columns.map(() => 1),
  columns,
  values
});

const buildUnlayerColumn = (id, contents, values = {}) => ({
  id,
  contents,
  values
});

const buildRowBackgroundValues = (backgroundColor, backgroundImageUrl) => {
  const values = {
    backgroundColor: toText(backgroundColor) || '#1f4d8f'
  };

  if (toText(backgroundImageUrl)) {
    values.backgroundImage = {
      url: toText(backgroundImageUrl),
      size: 'cover',
      position: 'center center',
      repeat: 'no-repeat'
    };
  }

  return values;
};

const isLikelyImageField = (key = '', label = '') => {
  const token = `${String(key).toLowerCase()} ${String(label).toLowerCase()}`;
  return /(image|icon|logo|background)/.test(token);
};

const titleCase = (value) => String(value || '')
  .replace(/([A-Z])/g, ' $1')
  .replace(/[_-]+/g, ' ')
  .replace(/\s+/g, ' ')
  .trim()
  .replace(/^./, (m) => m.toUpperCase());

const buildUnlayerDesignFromTemplate = ({ template, fields = [], customizations, selectedFont, logoUrl }) => {
  const now = Date.now();
  const baseFont = toText(selectedFont) || 'Arial, Helvetica, sans-serif';
  const primaryColor = firstNonEmpty(customizations.primaryColor, '#1f4d8f');
  const accentColor = firstNonEmpty(customizations.accentColor, '#2563eb');
  const backgroundColor = firstNonEmpty(customizations.backgroundColor, '#ffffff');
  const consumedImageKeys = new Set();

  const imageEntries = (fields.length > 0
    ? fields
      .filter((field) => isLikelyImageField(field?.key, field?.label))
      .map((field) => ({
        key: field.key,
        label: field.label,
        url: toText(customizations[field.key])
      }))
    : Object.keys(customizations)
      .filter((key) => isLikelyImageField(key, key))
      .map((key) => ({
        key,
        label: titleCase(key),
        url: toText(customizations[key])
      })))
    .filter((entry) => /^https?:\/\//i.test(entry.url));

  const heroTitle = firstNonEmpty(
    customizations.headline,
    customizations.eventTitle,
    customizations.headerTitle,
    customizations.heroTitle,
    customizations.templateTitle,
    customizations.newsletterTitle,
    customizations.updateTitle,
    customizations.mainTitle,
    template?.name,
    'Email Template'
  );

  const subtitle = [
    customizations.subtitle,
    customizations.heroSubtitle,
    customizations.issueInfo,
    customizations.updateSubtitle,
    customizations.detailLine1,
    customizations.detailLine2,
    customizations.detailLine3
  ].map((item) => toText(item)).filter(Boolean).join(' • ');

  const mainContent = firstNonEmpty(
    customizations.mainContent,
    customizations.featuredContent,
    customizations.introduction,
    customizations.content,
    customizations.message,
    'Update this section with your main message.'
  );

  const footerText = firstNonEmpty(
    customizations.footerText,
    'You are receiving this email because you opted in.'
  );

  const ctaText = firstNonEmpty(customizations.ctaText, 'Learn More');
  const ctaUrl = firstNonEmpty(customizations.ctaUrl, customizations.featuredUrl, 'https://example.com');

  const headerBackgroundImage = firstNonEmpty(customizations.headerBackgroundImage);
  const heroImage = firstNonEmpty(customizations.heroImage, customizations.imageUrl, customizations.bannerImage);
  const effectiveLogoUrl = firstNonEmpty(customizations.logoImageUrl, logoUrl);

  const rows = [];

  if (effectiveLogoUrl) {
    consumedImageKeys.add('logoImageUrl');
    rows.push(buildUnlayerRow(`logo-row-${now}`, [
      buildUnlayerColumn(`logo-col-${now}`, [
        buildUnlayerImageContent(`logo-image-${now}`, effectiveLogoUrl, '120px')
      ], { padding: '10px 24px 0px' })
    ]));
  }

  if (headerBackgroundImage) {
    consumedImageKeys.add('headerBackgroundImage');
  }

  rows.push(buildUnlayerRow(`hero-row-${now}`, [
    buildUnlayerColumn(`hero-col-${now}`, [
      buildUnlayerTextContent(`hero-title-${now}`, heroTitle, {
        fontSize: '30px',
        lineHeight: '130%',
        color: '#ffffff',
        textAlign: 'center',
        fontWeight: '700'
      }),
      ...(subtitle ? [buildUnlayerTextContent(`hero-subtitle-${now}`, subtitle, {
        fontSize: '15px',
        lineHeight: '150%',
        color: '#e6efff',
        textAlign: 'center',
        containerPadding: '8px 0px 0px'
      })] : [])
    ], { padding: '28px 24px' })
  ], buildRowBackgroundValues(primaryColor, headerBackgroundImage)));

  if (heroImage) {
    if (toText(customizations.heroImage)) {
      consumedImageKeys.add('heroImage');
    }
    rows.push(buildUnlayerRow(`image-row-${now}`, [
      buildUnlayerColumn(`image-col-${now}`, [
        buildUnlayerImageContent(`hero-image-${now}`, heroImage)
      ], { padding: '0px' })
    ]));
  }

  rows.push(buildUnlayerRow(`content-row-${now}`, [
    buildUnlayerColumn(`content-col-${now}`, [
      buildUnlayerTextContent(`main-content-${now}`, mainContent)
    ], { padding: '28px 24px 10px' })
  ]));

  rows.push(buildUnlayerRow(`cta-row-${now}`, [
    buildUnlayerColumn(`cta-col-${now}`, [
      buildUnlayerButtonContent(`cta-button-${now}`, ctaText, ctaUrl, accentColor)
    ], { padding: '6px 24px 26px' })
  ]));

  const responseOneLabel = toText(customizations.responseOneLabel);
  const responseTwoLabel = toText(customizations.responseTwoLabel);
  const responseThreeLabel = toText(customizations.responseThreeLabel);
  const responseOneUrl = firstNonEmpty(customizations.rsvpYesUrl, '{{acceptLink}}');
  const responseTwoUrl = firstNonEmpty(customizations.rsvpMaybeUrl, 'https://example.com/maybe');
  const responseThreeUrl = firstNonEmpty(customizations.rsvpNoUrl, 'https://example.com/no');

  if (responseOneLabel || responseTwoLabel || responseThreeLabel) {
    rows.push(buildUnlayerRow(`responses-row-${now}`, [
      buildUnlayerColumn(`responses-col-1-${now}`, [
        buildUnlayerButtonContent(`responses-btn-1-${now}`, responseOneLabel || 'Yes', responseOneUrl, '#15803d')
      ], { padding: '6px 6px 6px 0px' }),
      buildUnlayerColumn(`responses-col-2-${now}`, [
        buildUnlayerButtonContent(`responses-btn-2-${now}`, responseTwoLabel || 'Maybe', responseTwoUrl, '#b45309')
      ], { padding: '6px' }),
      buildUnlayerColumn(`responses-col-3-${now}`, [
        buildUnlayerButtonContent(`responses-btn-3-${now}`, responseThreeLabel || 'No', responseThreeUrl, '#b91c1c')
      ], { padding: '6px 0px 6px 6px' })
    ], {
      backgroundColor: '#f8fafc'
    }));
  }

  const card1Title = toText(customizations.story1Title);
  const card2Title = toText(customizations.story2Title);
  const card3Title = toText(customizations.story3Title);
  if (card1Title || card2Title || card3Title) {
    rows.push(buildUnlayerRow(`cards-row-${now}`, [
      buildUnlayerColumn(`cards-col-1-${now}`, [
        buildUnlayerTextContent(`cards-title-1-${now}`, card1Title || 'Card One', {
          fontSize: '15px',
          fontWeight: '700'
        }),
        buildUnlayerTextContent(`cards-copy-1-${now}`, toText(customizations.story1Preview) || 'Short summary line.', {
          fontSize: '13px',
          color: '#6b7280',
          containerPadding: '6px 0px 0px'
        })
      ], { padding: '0px 8px 0px 0px' }),
      buildUnlayerColumn(`cards-col-2-${now}`, [
        buildUnlayerTextContent(`cards-title-2-${now}`, card2Title || 'Card Two', {
          fontSize: '15px',
          fontWeight: '700'
        }),
        buildUnlayerTextContent(`cards-copy-2-${now}`, toText(customizations.story2Preview) || 'Short summary line.', {
          fontSize: '13px',
          color: '#6b7280',
          containerPadding: '6px 0px 0px'
        })
      ], { padding: '0px 4px' }),
      buildUnlayerColumn(`cards-col-3-${now}`, [
        buildUnlayerTextContent(`cards-title-3-${now}`, card3Title || 'Card Three', {
          fontSize: '15px',
          fontWeight: '700'
        }),
        buildUnlayerTextContent(`cards-copy-3-${now}`, toText(customizations.story3Preview) || 'Short summary line.', {
          fontSize: '13px',
          color: '#6b7280',
          containerPadding: '6px 0px 0px'
        })
      ], { padding: '0px 0px 0px 8px' })
    ], {
      backgroundColor: '#ffffff'
    }));
  }

  const speakerRows = [1, 2, 3].map((index) => {
    const imageKey = `speaker${index}Image`;
    const nameKey = `speaker${index}Name`;
    const roleKey = `speaker${index}Role`;
    const image = toText(customizations[imageKey]);
    const name = toText(customizations[nameKey]);
    const role = toText(customizations[roleKey]);
    if (!image && !name && !role) return null;
    consumedImageKeys.add(imageKey);
    return buildUnlayerColumn(`speaker-col-${index}-${now}`, [
      ...(image ? [buildUnlayerImageContent(`speaker-image-${index}-${now}`, image, '92px')] : []),
      ...(name ? [buildUnlayerTextContent(`speaker-name-${index}-${now}`, name, {
        fontSize: '13px',
        lineHeight: '140%',
        textAlign: 'center',
        fontWeight: '700',
        color: '#0f172a',
        containerPadding: '8px 0px 0px'
      })] : []),
      ...(role ? [buildUnlayerTextContent(`speaker-role-${index}-${now}`, role, {
        fontSize: '12px',
        lineHeight: '140%',
        textAlign: 'center',
        color: '#64748b',
        containerPadding: '4px 0px 0px'
      })] : [])
    ], { padding: '0px 6px' });
  }).filter(Boolean);

  if (speakerRows.length > 0) {
    rows.push(buildUnlayerRow(`speakers-row-${now}`, speakerRows, { backgroundColor: '#ffffff' }));
  }

  const remainingImages = imageEntries.filter((entry) => !consumedImageKeys.has(entry.key));
  if (remainingImages.length > 0) {
    rows.push(buildUnlayerRow(`visual-assets-title-row-${now}`, [
      buildUnlayerColumn(`visual-assets-title-col-${now}`, [
        buildUnlayerTextContent(`visual-assets-title-${now}`, 'Visual Assets', {
          fontSize: '14px',
          fontWeight: '700',
          color: '#334155'
        })
      ], { padding: '8px 24px 6px' })
    ]));

    remainingImages.forEach((entry, index) => {
      rows.push(buildUnlayerRow(`visual-asset-row-${index}-${now}`, [
        buildUnlayerColumn(`visual-asset-col-${index}-${now}`, [
          buildUnlayerTextContent(`visual-asset-label-${index}-${now}`, titleCase(entry.label || entry.key), {
            fontSize: '12px',
            color: '#64748b',
            containerPadding: '0px 0px 6px'
          }),
          buildUnlayerImageContent(`visual-asset-image-${index}-${now}`, entry.url, '560px')
        ], { padding: '0px 24px 12px' })
      ]));
    });
  }

  rows.push(buildUnlayerRow(`footer-row-${now}`, [
    buildUnlayerColumn(`footer-col-${now}`, [
      buildUnlayerTextContent(`footer-text-${now}`, footerText, {
        fontSize: '12px',
        lineHeight: '160%',
        color: '#6b7280',
        textAlign: 'center'
      })
    ], { padding: '16px 18px' })
  ], {
    backgroundColor: '#f6f7f9'
  }));

  return {
    counters: {},
    body: {
      id: `template-body-${now}`,
      rows,
      values: {
        backgroundColor,
        contentWidth: '600px',
        fontFamily: {
          label: 'Custom',
          value: baseFont
        }
      }
    }
  };
};

const formatEventDate = (value, eventTimeZone) => formatEventDateTimeDisplay(value, eventTimeZone);

const getFieldEventValue = (field, eventContext) => {
  if (!field || !eventContext) return '';

  const key = String(field.key || '').toLowerCase();
  const label = String(field.label || '').toLowerCase();
  const token = `${key} ${label}`;

  const eventTitle = String(eventContext.Title || '').trim();
  const eventDescription = String(eventContext.Description || '').trim();
  const eventDateDisplay = String(
    eventContext.EventDateDisplay || formatEventDate(eventContext.EventDate, eventContext.EventTimeZone) || ''
  ).trim();
  const eventLocation = String(eventContext.Location || '').trim();
  const eventVirtualLink = String(eventContext.VirtualLink || '').trim();
  const eventContact = String(eventContext.EventContact || '').trim();

  if (token.includes('ctaurl') || token.includes('button url') || token.includes('link url')) {
    return eventVirtualLink || '';
  }

  if (token.includes('ctatext') || token.includes('button text')) {
    return 'RSVP Now';
  }

  if (token.includes('date') || token.includes('time')) {
    return eventDateDisplay || '';
  }

  if (token.includes('location') || token.includes('venue')) {
    return eventLocation || eventVirtualLink || '';
  }

  if (token.includes('subtitle') || token.includes('tagline') || token.includes('eyebrow')) {
    return eventDateDisplay || eventLocation || '';
  }

  if (token.includes('title') || token.includes('headline')) {
    return eventTitle || '';
  }

  if (token.includes('footer')) {
    return eventContact
      ? `Questions? Contact ${eventContact}.`
      : 'We look forward to seeing you there.';
  }

  if (token.includes('content') || token.includes('message') || token.includes('description') || token.includes('body')) {
    const details = [
      eventDescription,
      eventDateDisplay ? `Date & Time: ${eventDateDisplay}` : '',
      eventLocation ? `Location: ${eventLocation}` : '',
      eventVirtualLink ? `Join link: ${eventVirtualLink}` : ''
    ].filter(Boolean);
    return details.join('\n\n');
  }

  return '';
};

const TemplateCustomizer = ({ template, onApply, onBack, onUseStyleInAI, eventContext = null, isLoading = false }) => {
  const [fields, setFields] = useState([]);
  const [customizations, setCustomizations] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [applying, setApplying] = useState(false);
  const [generatingAI, setGeneratingAI] = useState(false);
  const [activeStep, setActiveStep] = useState('layout');
  const [uploadingField, setUploadingField] = useState(null);
  const [selectedFont, setSelectedFont] = useState('');
  const [imageLoadErrors, setImageLoadErrors] = useState({});
  const [rscLogoVariant, setRscLogoVariant] = useState(null); // null | 'white' | 'blue'
  const [backgroundImageMode, setBackgroundImageMode] = useState('section'); // section | image
  const [backgroundImageSize, setBackgroundImageSize] = useState('contain'); // cover | contain | 75% | 100% | 125% | 150%
  const [livePreviewHtml, setLivePreviewHtml] = useState('');
  const [previewRefreshing, setPreviewRefreshing] = useState(false);
  const previewRequestRef = useRef(0);

  const templateHasLogoField = useMemo(
    () => fields.some((field) => {
      const key = String(field.key || '').toLowerCase();
      const label = String(field.label || '').toLowerCase();
      return key.includes('logo') || label.includes('logo');
    }),
    [fields]
  );

  const groupedFields = useMemo(() => {
    const groups = { typography: [], colors: [], images: [], content: [] };
    fields.forEach((field) => groups[classifyField(field)].push(field));
    return groups;
  }, [fields]);

  const hasHeaderBackgroundField = useMemo(
    () => fields.some((field) => String(field?.key || '').toLowerCase() === 'headerbackgroundimage'),
    [fields]
  );

  const currentStepIndex = STEP_ORDER.indexOf(activeStep);

  const reviewSummary = useMemo(() => {
    const typographyItems = summarizeFields(groupedFields.typography, customizations);

    // Typography applies globally even when a layout has no explicit typography fields.
    if (!typographyItems.length) {
      typographyItems.push({
        key: '__global_font_family',
        label: 'Global Font Family',
        value: selectedFont || '(not selected)',
        rawValue: selectedFont || '',
        imageSrc: '',
        isEmpty: !selectedFont,
      });
    }

    return {
      typography: typographyItems,
      colors: summarizeFields(groupedFields.colors, customizations),
      images: summarizeFields(groupedFields.images, customizations),
      content: summarizeFields(groupedFields.content, customizations),
    };
  }, [groupedFields, customizations, selectedFont]);

  useEffect(() => {
    if (!template) return;

    const loadFields = async () => {
      try {
        setLoading(true);
        setError(null);
        setPreviewHtml(null);
        setShowPreview(false);
        setActiveStep('layout');

        const response = await api.get(`/templates/library/${template.id}/fields`);
        if (!response.data.success) {
          setError('Failed to load template fields');
          return;
        }

        const loadedFields = response.data.fields || [];
        setFields(loadedFields);
        setBackgroundImageMode('section');
        setBackgroundImageSize('contain');

        const defaults = {};
        loadedFields.forEach((field) => {
          defaults[field.key] = field.default || '';
        });

        const eventSeed = loadedFields.reduce((acc, field) => {
          const seededValue = getFieldEventValue(field, eventContext);
          if (seededValue) {
            acc[field.key] = seededValue;
          }
          return acc;
        }, {});

        setCustomizations({ ...defaults, ...eventSeed });

        const templateFontField = loadedFields.find((field) => String(field.key || '').toLowerCase().includes('font'));
        setSelectedFont(templateFontField?.default || '');
      } catch (err) {
        console.error('Error loading fields:', err);
        setError(err.message || 'Error loading template fields');
      } finally {
        setLoading(false);
      }
    };

    loadFields();
  }, [template]);

  const handleFieldChange = (key, value) => {
    setCustomizations((prev) => ({ ...prev, [key]: value }));
    setImageLoadErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const buildScaffoldPreview = async () => {
    const finalCustomizations = getFinalCustomizations();
    const shouldInjectInlineLogo = !templateHasLogoField ? rscLogoVariant : null;

    const response = await api.post('/templates/apply-library-template', {
      templateId: template.id,
      customizations: finalCustomizations,
      title: `${template.name} - Preview`,
      preservePlaceholders: false,
      selectedFont,
      logoUrl: rscLogoVariant ? RSC_LOGOS[rscLogoVariant] : '',
      backgroundImageMode,
      backgroundImageSize
    });

    if (!response.data.success) {
      throw new Error('Failed to generate scaffold preview');
    }

    return injectRscLogo(injectGlobalFont(response.data.html, selectedFont), shouldInjectInlineLogo);
  };

  const getFinalCustomizations = () => {
    const next = { ...customizations };
    let hasFontField = false;

    fields.forEach((field) => {
      const key = String(field.key || '').toLowerCase();
      const label = String(field.label || '').toLowerCase();
      const isFontField = key.includes('font') || label.includes('font');
      if (isFontField) {
        next[field.key] = selectedFont;
        hasFontField = true;
      }
    });

    const withTypography = hasFontField ? next : { ...customizations };

    if (rscLogoVariant) {
      const logoUrl = RSC_LOGOS[rscLogoVariant];
      fields.forEach((field) => {
        const key = String(field.key || '').toLowerCase();
        const label = String(field.label || '').toLowerCase();
        if (key.includes('logo') || label.includes('logo')) {
          withTypography[field.key] = logoUrl;
        }
      });
    }

    // Prevent broken-image boxes in builder/preview by swapping unresolved image fields
    // with an inline SVG placeholder when URLs fail to load or are empty.
    fields.forEach((field) => {
      if (!isImageField(field)) return;
      const hasKnownLoadError = Boolean(imageLoadErrors[field.key]);
      withTypography[field.key] = resolveImageForOutput(withTypography[field.key], hasKnownLoadError);
    });

    return withTypography;
  };

  const handleUploadImage = async (fieldKey, file) => {
    if (!file) return;
    try {
      setUploadingField(fieldKey);
      const response = await templatesAPI.uploadImage(file);
      const imageUrl = response.data?.imageUrl;
      if (!imageUrl) throw new Error('Image upload did not return a URL');
      handleFieldChange(fieldKey, imageUrl);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to upload image');
    } finally {
      setUploadingField(null);
    }
  };

  const handleDeleteImage = async (fieldKey) => {
    const currentUrl = customizations[fieldKey];
    try {
      if (currentUrl && currentUrl.startsWith('http')) {
        await templatesAPI.deleteImage(currentUrl);
      }
    } catch (err) {
      console.warn('[TemplateCustomizer] Failed CDN image delete:', err.message);
    } finally {
      handleFieldChange(fieldKey, '');
      setImageLoadErrors((prev) => {
        if (!prev[fieldKey]) return prev;
        const next = { ...prev };
        delete next[fieldKey];
        return next;
      });
    }
  };

  const generatePreview = async () => {
    try {
      setApplying(true);
      setError(null);

      const html = await buildScaffoldPreview();
      setPreviewHtml(html);
      setLivePreviewHtml(html);
      setShowPreview(true);
      setActiveStep('assemble');
    } catch (err) {
      console.error('Error generating preview:', err);
      setError(err.message || 'Error generating preview');
    } finally {
      setApplying(false);
    }
  };

  useEffect(() => {
    if (!template?.id || loading || fields.length === 0) return;

    const requestId = previewRequestRef.current + 1;
    previewRequestRef.current = requestId;
    setPreviewRefreshing(true);

    const timer = setTimeout(async () => {
      try {
        const html = await buildScaffoldPreview();
        if (previewRequestRef.current !== requestId) return;
        setLivePreviewHtml(html);
      } catch (err) {
        // Keep this silent to avoid interrupting typing with transient preview errors.
      } finally {
        if (previewRequestRef.current === requestId) {
          setPreviewRefreshing(false);
        }
      }
    }, 600);

    return () => {
      clearTimeout(timer);
    };
  }, [template?.id, customizations, selectedFont, rscLogoVariant, backgroundImageMode, backgroundImageSize, templateHasLogoField, fields, loading]);

  const previewDocHtml = useMemo(() => {
    const html = livePreviewHtml || previewHtml || '';
    if (!html) return '';

    return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <style>
      html, body { margin: 0; padding: 0; background: #eef2f7; }
      .preview-root { width: 600px; margin: 0 auto; }
      img { max-width: 100%; height: auto; }
    </style>
  </head>
  <body>
    <div class="preview-root">${html}</div>
  </body>
</html>`;
  }, [livePreviewHtml, previewHtml]);

  const handleApplyScaffold = async () => {
    try {
      setApplying(true);
      setError(null);

      const finalCustomizations = getFinalCustomizations();
      const shouldInjectInlineLogo = !templateHasLogoField ? rscLogoVariant : null;
      const response = await api.post('/templates/apply-library-template', {
        templateId: template.id,
        customizations: finalCustomizations,
        title: template.name,
        preservePlaceholders: false,
        selectedFont,
        logoUrl: rscLogoVariant ? RSC_LOGOS[rscLogoVariant] : '',
        backgroundImageMode,
        backgroundImageSize
      });

      if (!response.data.success) {
        setError('Failed to apply template');
        return;
      }

      const design = response.data?.design;
      if (!design) {
        throw new Error('Template service did not return a Unlayer design payload');
      }

      onApply({
        templateId: template.id,
        templateName: template.name,
        html: injectRscLogo(injectGlobalFont(response.data.html, selectedFont), shouldInjectInlineLogo),
        design,
        mjml: response.data.mjml,
        customizations: finalCustomizations,
        source: 'library-wizard-scaffold',
      });
    } catch (err) {
      console.error('Error applying template:', err);
      setError(err.message || 'Error applying template');
    } finally {
      setApplying(false);
    }
  };

  const handleGenerateWithAI = async () => {
    try {
      setGeneratingAI(true);
      setError(null);

      const finalCustomizations = getFinalCustomizations();
      const shouldInjectInlineLogo = !templateHasLogoField ? rscLogoVariant : null;
      const preset = getAiPresetForTemplate(template);
      const typographySummary = groupedFields.typography
        .map((field) => `${field.label}: ${fieldValue(field, finalCustomizations)}`)
        .join('\n');
      const colorSummary = groupedFields.colors
        .map((field) => `${field.label}: ${fieldValue(field, finalCustomizations)}`)
        .join('\n');
      const imageSummary = groupedFields.images
        .map((field) => `${field.label}: ${fieldValue(field, finalCustomizations)}`)
        .join('\n');
      const contentSummary = groupedFields.content
        .map((field) => `${field.label}: ${fieldValue(field, finalCustomizations)}`)
        .join('\n');

      const details = [
        `Use this layout scaffold: ${template.name}.`,
        selectedFont ? `Global font family override: ${selectedFont}` : null,
        rscLogoVariant ? `Include the RSC ${rscLogoVariant} logo near the top of the email.` : null,
        `Typography:\n${typographySummary || 'No typography overrides provided.'}`,
        `Colors:\n${colorSummary || 'No color overrides provided.'}`,
        `Images:\n${imageSummary || 'No image URLs provided. Leave image slots empty if needed.'}`,
        `Content Assignments:\n${contentSummary || 'No copy provided yet.'}`,
        'Respect provided values and fill any missing sections coherently.',
      ].filter(Boolean).join('\n\n');

      const response = await templatesAPI.aiDesign({
        purpose: `Assemble ${template.name} using provided wizard inputs`,
        tone: 'professional',
        type: preset.type,
        details,
        designStyle: preset.designStyle,
        layoutStyle: preset.layoutStyle,
        usePexelsImages: false,
        styleProfile: {
          ...(preset.styleProfile || {}),
          sourceTemplateId: template.id,
          sourceTemplateName: template.name,
          wizardInputs: finalCustomizations,
        },
        templateName: template.name,
        customizations: finalCustomizations,
        selectedFont,
        logoUrl: rscLogoVariant ? RSC_LOGOS[rscLogoVariant] : '',
        backgroundImageMode,
        backgroundImageSize,
        context: {
          title: template.name,
          description: template.description,
          subject: '',
        },
      });

      const copy = response.data?.copy;
      if (!copy?.html) throw new Error('AI response did not include HTML output');

      const design = response.data?.design;
      if (!design) throw new Error('AI design response did not include a Unlayer design payload');

      onApply({
        templateId: template.id,
        templateName: `${template.name} (AI Assembled)`,
        html: injectRscLogo(injectGlobalFont(copy.html, selectedFont), shouldInjectInlineLogo),
        design,
        mjml: null,
        customizations: finalCustomizations,
        subject: copy.subject || '',
        source: 'library-wizard-ai',
      });
    } catch (err) {
      console.error('Error generating AI assembled template:', err);
      setError(err.response?.data?.error || err.message || 'Failed to generate template with AI');
    } finally {
      setGeneratingAI(false);
    }
  };

  const renderField = (field) => {
    const value = fieldValue(field, customizations);

    const handleFocus = (e) => {
      if (e.target.value === (field.default || '')) {
        handleFieldChange(field.key, '');
      }
    };

    if (field.type === 'color') {
      const parsedColor = parseColorValue(value || '#000000');
      const alphaPercent = Math.round(parsedColor.alpha * 100);
      return (
        <div key={field.key} className="field-group">
          <label htmlFor={field.key} className="field-label">{field.label}</label>
          <div className="color-input-wrapper">
            <input
              type="color"
              id={field.key}
              value={parsedColor.hex}
              onChange={(e) => handleFieldChange(field.key, composeColorWithAlpha(e.target.value, alphaPercent))}
              className="color-input"
            />
            <span className="color-value">{value || '#000000'}</span>
          </div>
          <div className="color-input-wrapper" style={{ marginTop: '8px', alignItems: 'center', gap: '10px' }}>
            <label htmlFor={`${field.key}-opacity`} className="field-label" style={{ marginBottom: 0, minWidth: '72px' }}>
              Opacity
            </label>
            <input
              id={`${field.key}-opacity`}
              type="range"
              min="0"
              max="100"
              value={alphaPercent}
              onChange={(e) => handleFieldChange(field.key, composeColorWithAlpha(parsedColor.hex, e.target.value))}
              style={{ flex: 1 }}
            />
            <span className="color-value" style={{ minWidth: '56px', textAlign: 'right' }}>{alphaPercent}%</span>
          </div>
        </div>
      );
    }

    if (field.type === 'select') {
      const isFontSelect = String(field.key || '').toLowerCase().includes('font');
      return (
        <div key={field.key} className="field-group">
          <label htmlFor={field.key} className="field-label">{field.label}</label>
          <select
            id={field.key}
            value={isFontSelect && selectedFont ? selectedFont : value}
            onChange={(e) => {
              handleFieldChange(field.key, e.target.value);
              if (isFontSelect) setSelectedFont(e.target.value);
            }}
            className="field-input"
          >
            <option value="">Select {field.label}</option>
            {(field.options || []).map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </div>
      );
    }

    if (field.type === 'textarea') {
      return (
        <div key={field.key} className="field-group">
          <label htmlFor={field.key} className="field-label">{field.label}</label>
          <textarea
            id={field.key}
            value={value}
            onChange={(e) => handleFieldChange(field.key, e.target.value)}
            onFocus={handleFocus}
            className="field-input field-textarea"
            rows="4"
            placeholder={field.label}
          />
        </div>
      );
    }

    if (isImageField(field)) {
      const imageValue = String(value || '').trim();
      const hasUpload = Boolean(imageValue);
      const previewSrc = normalizeImagePreviewSrc(imageValue);
      const resolvedPreviewSrc = imageLoadErrors[field.key] ? LIBRARY_FALLBACK_IMAGE_URL : previewSrc;
      const showThumbnail = hasUpload && Boolean(resolvedPreviewSrc);
      return (
        <div key={field.key} className="field-group image-upload-slot">
          <label className="field-label">{field.label}</label>
          <div className="image-field-row">
            <div className="image-thumb-frame" aria-hidden="true">
              {showThumbnail ? (
                <img
                  src={resolvedPreviewSrc}
                  alt={field.label}
                  className="image-thumb"
                  onError={() => {
                    setImageLoadErrors((prev) => ({ ...prev, [field.key]: true }));
                  }}
                />
              ) : (
                <span className="image-thumb-placeholder">No image</span>
              )}
            </div>

            {hasUpload ? (
              <div className="image-preview-wrapper">
                <div className="image-preview-meta" title={imageValue}>{imageValue}</div>
                <div className="image-preview-actions">
                  <label className="upload-inline-button">
                    {uploadingField === field.key ? 'Uploading...' : 'Replace'}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleUploadImage(field.key, e.target.files?.[0])}
                      disabled={uploadingField === field.key}
                    />
                  </label>
                  <button
                    type="button"
                    className="image-delete-btn"
                    onClick={() => handleDeleteImage(field.key)}
                    title="Remove image and delete from CDN"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ) : (
              <div className="image-upload-prompt">
                <label className="upload-inline-button upload-primary">
                  {uploadingField === field.key ? 'Uploading...' : 'Upload image'}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleUploadImage(field.key, e.target.files?.[0])}
                    disabled={uploadingField === field.key}
                  />
                </label>
                <span className="upload-or">or paste URL</span>
                <input
                  type="text"
                  value={value}
                  onChange={(e) => handleFieldChange(field.key, e.target.value)}
                  onFocus={handleFocus}
                  className="field-input image-url-input"
                  placeholder="https://..."
                />
                {imageLoadErrors[field.key] && (
                  <span className="upload-inline-status">Image URL failed to load. A fallback image will be used until replaced.</span>
                )}
              </div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div key={field.key} className="field-group">
        <label htmlFor={field.key} className="field-label">{field.label}</label>
        <input
          type="text"
          id={field.key}
          value={value}
          onChange={(e) => handleFieldChange(field.key, e.target.value)}
          onFocus={handleFocus}
          className="field-input"
          placeholder={field.label}
        />
      </div>
    );
  };

  const renderStepFields = () => {
    if (activeStep === 'layout') {
      return (
        <div className="wizard-layout-card">
          <p>Selected layout scaffold:</p>
          <h4>{template.name}</h4>
          <p>{template.description}</p>
          {hasHeaderBackgroundField ? (
            <div className="field-group" style={{ marginTop: '12px' }}>
              <label htmlFor="background-image-mode" className="field-label">Background Image Behavior</label>
              <select
                id="background-image-mode"
                className="field-input"
                value={backgroundImageMode}
                onChange={(e) => setBackgroundImageMode(e.target.value)}
              >
                <option value="section">Use as hero section background</option>
                <option value="image">Insert as resizable image block</option>
              </select>
              <p className="wizard-layout-note" style={{ marginTop: '8px' }}>
                Section background matches original template look. Image block gives direct resize controls in Unlayer.
              </p>
              {backgroundImageMode === 'section' && (
                <div style={{ marginTop: '12px' }}>
                  <label htmlFor="background-image-size" className="field-label">Background Image Zoom</label>
                  <select
                    id="background-image-size"
                    className="field-input"
                    value={backgroundImageSize}
                    onChange={(e) => setBackgroundImageSize(e.target.value)}
                  >
                    <option value="cover">Fill (Cover)</option>
                    <option value="contain">Fit (Contain)</option>
                    <option value="75%">Zoom out — 75%</option>
                    <option value="100%">Actual size — 100%</option>
                    <option value="125%">Zoom in — 125%</option>
                    <option value="150%">Zoom in — 150%</option>
                    <option value="200%">Zoom in — 200%</option>
                  </select>
                </div>
              )}
            </div>
          ) : null}
          <p className="wizard-layout-note">Choose another layout from the library if needed, otherwise continue to style details.</p>
        </div>
      );
    }

    if (activeStep === 'assemble') {
      return (
        <div className="wizard-layout-card">
          <p>Ready to build your final template.</p>
          <h4>Assembly Options</h4>
          <p>Use scaffold apply for deterministic output, or use AI assembly to merge your selections into a fully composed draft.</p>
        </div>
      );
    }

    if (activeStep === 'review') {
      return (
        <div className="wizard-review-grid">
          {[
            { key: 'typography', label: 'Typography' },
            { key: 'colors', label: 'Colors' },
            { key: 'images', label: 'Images' },
            { key: 'content', label: 'Content' },
          ].map((section) => {
            const items = reviewSummary[section.key] || [];
            const emptyCount = items.filter((item) => item.isEmpty).length;
            return (
              <div key={section.key} className="wizard-review-card">
                <div className="wizard-review-card-header">
                  <h4>{section.label}</h4>
                  <span>{items.length} fields</span>
                </div>
                {items.length === 0 ? (
                  <p className="wizard-review-empty">No {section.label.toLowerCase()} fields for this layout.</p>
                ) : (
                  <div className="wizard-review-list">
                    {items.map((item) => (
                      <div key={item.key} className={`wizard-review-item ${item.isEmpty ? 'missing' : ''}`}>
                        <span className="wizard-review-label">{item.label}</span>
                        {section.key === 'images' && item.imageSrc && (
                          <img
                            src={item.imageSrc}
                            alt={item.label}
                            className="wizard-review-thumb"
                            onError={(event) => {
                              event.currentTarget.onerror = null;
                              event.currentTarget.src = LIBRARY_FALLBACK_IMAGE_URL;
                            }}
                          />
                        )}
                        <span className="wizard-review-value">{item.value}</span>
                      </div>
                    ))}
                  </div>
                )}
                {emptyCount > 0 && <p className="wizard-review-warning">{emptyCount} field(s) still empty.</p>}
              </div>
            );
          })}
        </div>
      );
    }

    if (activeStep === 'typography') {
      const typoFields = groupedFields.typography || [];
      return (
        <>
          <div className="font-selector-group">
            <label className="field-label">Base Font Family</label>
            <p className="font-selector-hint">Select a browser-safe font. It will be applied to scaffold preview/output.</p>
            <div className="font-chip-row">
              {FONT_FAMILIES.map((font) => (
                <button
                  key={font.value}
                  type="button"
                  className={`font-chip ${selectedFont === font.value ? 'active' : ''}`}
                  style={{ fontFamily: font.value }}
                  onClick={() => setSelectedFont(font.value)}
                  title={font.value}
                >
                  {font.label}
                </button>
              ))}
            </div>
            {selectedFont && (
              <p className="font-preview-text" style={{ fontFamily: selectedFont }}>
                The quick brown fox jumps over the lazy dog. 0123456789
              </p>
            )}
          </div>
          {typoFields.map((field) => renderField(field))}
        </>
      );
    }

    if (activeStep === 'images') {
      const imageFields = groupedFields.images || [];
      if (!imageFields.length) {
        return <p className="no-fields">This layout has no named image slots. Pick another layout to use image placeholders.</p>;
      }
      return <>{imageFields.map((field) => renderField(field))}</>;
    }

    const stepFields = groupedFields[activeStep] || [];
    if (!stepFields.length) {
      return <p className="no-fields">No fields in this section for this layout.</p>;
    }

    return <>{stepFields.map((field) => renderField(field))}</>;
  };

  if (!template) {
    return (
      <div className="template-customizer">
        <div className="error-state">
          <p>No template selected</p>
          <button type="button" className="back-button" onClick={onBack}>Back to Templates</button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="template-customizer">
        <div className="loading-state">
          <p>Loading wizard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="template-customizer">
      <div className="customizer-header">
        <div>
          <h2>{template.name}</h2>
          <p className="template-description">{template.description}</p>
        </div>
        <button type="button" className="back-button" onClick={onBack} title="Back to template library">
          Back
        </button>
      </div>

      <div className="wizard-steps-row">
        {STEP_ORDER.map((step, index) => (
          <button
            key={step}
            type="button"
            className={`wizard-step-chip ${activeStep === step ? 'active' : ''}`}
            onClick={() => setActiveStep(step)}
          >
            <span>{index + 1}</span>{STEP_META[step].label}
          </button>
        ))}
      </div>

      <div className="customizer-layout">
        <div className="customizer-form">
          <div className="form-section">
            <h3>{STEP_META[activeStep].label}</h3>
            <p className="step-description">{STEP_META[activeStep].description}</p>

            <div style={{ marginBottom: '12px' }}>
              <span style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#334155', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Add RSC Logo</span>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                <button
                  type="button"
                  onClick={() => setRscLogoVariant(rscLogoVariant === 'white' ? null : 'white')}
                  style={{
                    padding: '5px 12px',
                    fontSize: '12px',
                    fontWeight: '700',
                    border: `1px solid ${rscLogoVariant === 'white' ? '#3b82f6' : '#cbd5e1'}`,
                    borderRadius: '999px',
                    background: rscLogoVariant === 'white' ? '#dbeafe' : '#f8fafc',
                    color: rscLogoVariant === 'white' ? '#1d4ed8' : '#475569',
                    cursor: 'pointer'
                  }}
                >
                  {rscLogoVariant === 'white' ? '✓ ' : ''}White Logo
                </button>
                <button
                  type="button"
                  onClick={() => setRscLogoVariant(rscLogoVariant === 'blue' ? null : 'blue')}
                  style={{
                    padding: '5px 12px',
                    fontSize: '12px',
                    fontWeight: '700',
                    border: `1px solid ${rscLogoVariant === 'blue' ? '#3b82f6' : '#cbd5e1'}`,
                    borderRadius: '999px',
                    background: rscLogoVariant === 'blue' ? '#dbeafe' : '#f8fafc',
                    color: rscLogoVariant === 'blue' ? '#1d4ed8' : '#475569',
                    cursor: 'pointer'
                  }}
                >
                  {rscLogoVariant === 'blue' ? '✓ ' : ''}Blue Logo
                </button>
              </div>
            </div>

            {error && (
              <div className="error-message">
                <span className="error-icon">!</span>
                <span>{error}</span>
                <button type="button" className="clear-error" onClick={() => setError(null)}>x</button>
              </div>
            )}

            <div className="fields-container">{renderStepFields()}</div>
          </div>

          <div className="customizer-actions wizard-actions">
            <button
              type="button"
              className="preview-button"
              onClick={() => setActiveStep(STEP_ORDER[Math.max(0, currentStepIndex - 1)])}
              disabled={currentStepIndex === 0}
            >
              Previous
            </button>
            <button
              type="button"
              className="preview-button"
              onClick={() => setActiveStep(STEP_ORDER[Math.min(STEP_ORDER.length - 1, currentStepIndex + 1)])}
              disabled={currentStepIndex === STEP_ORDER.length - 1}
            >
              Next
            </button>
          </div>
        </div>

        <div className="customizer-preview">
          <div className="scaffold-preview-panel">
            <div className="scaffold-preview-header">
              <div>
                <span className="scaffold-preview-kicker">Active Scaffold Preview</span>
                <h3>{template.name}</h3>
                <p>Live preview refreshes as you edit fields so the right pane reflects current scaffold output.</p>
              </div>
              <button
                type="button"
                className="scaffold-preview-ai-button"
                onClick={() => onUseStyleInAI && onUseStyleInAI(template)}
              >
                Open AI Assist
              </button>
            </div>

            <div className="scaffold-preview-viewport">
              {previewDocHtml ? (
                <iframe
                  className="scaffold-preview-frame"
                  title="Active scaffold preview"
                  srcDoc={previewDocHtml}
                  sandbox=""
                />
              ) : (
                <div className="scaffold-preview-empty">Preview will appear after field values are loaded.</div>
              )}
              {previewRefreshing && <div className="scaffold-preview-refreshing">Refreshing preview...</div>}
            </div>
          </div>

          <div className="wizard-assemble-actions">
            <button type="button" className="preview-button" onClick={generatePreview} disabled={applying || generatingAI || isLoading}>
              {applying ? 'Generating preview...' : 'Preview Scaffold'}
            </button>
            <button type="button" className="apply-button" onClick={handleApplyScaffold} disabled={applying || generatingAI || isLoading}>
              {applying ? 'Applying...' : 'Apply to Builder'}
            </button>
            <button type="button" className="apply-button wizard-ai-button" onClick={handleGenerateWithAI} disabled={applying || generatingAI || isLoading}>
              {generatingAI ? 'Generating AI...' : 'Generate with AI'}
            </button>
          </div>

          {showPreview ? (
            <div className="preview-container">
              <label className="preview-label">Wizard Preview</label>
              <div className="preview-frame" dangerouslySetInnerHTML={{ __html: previewHtml }} />
            </div>
          ) : (
            <div className="preview-placeholder">
              <p>Run Preview Scaffold to validate your current wizard selections.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TemplateCustomizer;
